import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import posthog from 'posthog-js';

/**
 * Android notification channel IDs.
 *
 * Push notifications (FCM) use ANDROID_PUSH_CHANNEL_ID – the backend sends this
 * channel_id in the AndroidNotification when the `android-push-notification-channel`
 * feature flag is enabled.
 *
 * Local notifications for cycle/symptom tracking use ANDROID_TRACKING_CHANNEL_ID,
 * created here when the `android-local-notification-channel` feature flag is enabled.
 * The prevention-journey local notifications are excluded from this experiment.
 */
export const ANDROID_PUSH_CHANNEL_ID = 'gynger-chat';
export const ANDROID_TRACKING_CHANNEL_ID = 'gynger-tracking';

const PUSH_CHANNEL_FEATURE_FLAG = 'android-push-notification-channel';
const LOCAL_CHANNEL_FEATURE_FLAG = 'android-local-notification-channel';

/**
 * Creates Android notification channels when on Android and the respective
 * feature flags are enabled.  Safe to call multiple times – the OS silently
 * ignores duplicate channel creation for an existing channel ID.
 *
 * Must be called after the user is authenticated and feature flags are loaded.
 */
export async function createNotificationChannels(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') {
    return;
  }

  if (!Capacitor.isPluginAvailable('LocalNotifications')) {
    return;
  }

  const localChannelEnabled = posthog.getFeatureFlag(LOCAL_CHANNEL_FEATURE_FLAG) === true;
  const pushChannelEnabled = posthog.getFeatureFlag(PUSH_CHANNEL_FEATURE_FLAG) === true;

  if (localChannelEnabled) {
    await LocalNotifications.createChannel({
      id: ANDROID_TRACKING_CHANNEL_ID,
      name: 'Suivi du cycle et des symptômes',
      description: 'Rappels de suivi du cycle et des symptômes',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  }

  if (pushChannelEnabled) {
    await LocalNotifications.createChannel({
      id: ANDROID_PUSH_CHANNEL_ID,
      name: 'Messages Gynger',
      description: 'Notifications de nouveaux messages Gynger',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
  }
}

/**
 * Returns the channel ID to use for cycle/symptom tracking local notifications
 * when the feature flag is enabled on Android, or undefined otherwise (falls
 * back to the OS default).
 *
 * Prevention-journey notifications are excluded from this experiment and should
 * NOT use this channel ID.
 */
export function getTrackingNotificationChannelId(): string | undefined {
  if (Capacitor.getPlatform() !== 'android') {
    return undefined;
  }
  if (posthog.getFeatureFlag(LOCAL_CHANNEL_FEATURE_FLAG) === true) {
    return ANDROID_TRACKING_CHANNEL_ID;
  }
  return undefined;
}
